import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, IconButton, Divider, CssBaseline } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ForumIcon from '@mui/icons-material/Forum';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import InsightsIcon from '@mui/icons-material/Insights';
import LogoutIcon from '@mui/icons-material/Logout';
import PeopleIcon from '@mui/icons-material/People';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import GavelIcon from '@mui/icons-material/Gavel';
import EmailIcon from '@mui/icons-material/Email';
import { useDispatch } from 'react-redux';
import { logout } from '../store/actions';
import Header from '../ui-component/Header';
import logo from 'assets/images/logo.jpg';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';

const drawerWidth = 240;
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 5 minutes

const menu = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
  // { to: '/admin/onboarding', label: 'Onboarding', icon: <RocketLaunchIcon /> },
  { to: '/admin/users', label: 'Users', icon: <PeopleIcon /> },
  { to: '/admin/moderation', label: 'Community Moderation', icon: <ForumIcon /> },
  { to: '/admin/legal', label: 'Content Management', icon: <GavelIcon /> },
  { to: '/admin/moods', label: 'Moods & Symptoms', icon: <SentimentSatisfiedAltIcon /> },
  // { to: '/admin/journal', label: 'Journal', icon: <MenuBookIcon /> },
  // { to: '/admin/notifications', label: 'Push Notifications', icon: <NotificationsIcon /> },
  // { to: '/admin/subscriptions', label: 'Subscriptions & Billing', icon: <CreditCardIcon /> },
  { to: '/admin/newsletter-subscribers', label: 'Newsletter Subscribers', icon: <EmailIcon /> },
  { to: '/admin/analytics', label: 'Analytics', icon: <InsightsIcon /> },
];

const AdminLayout = () => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const idleTimerRef = React.useRef(null);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const resetIdleTimer = React.useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    idleTimerRef.current = setTimeout(() => {
      handleLogout();
    }, IDLE_TIMEOUT_MS);
  }, [handleLogout]);

  React.useEffect(() => {
    resetIdleTimer();

    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    const onActivity = () => resetIdleTimer();
    activityEvents.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));

    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      activityEvents.forEach((evt) => window.removeEventListener(evt, onActivity));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box className="">
        <div className="sidebar-logo" />
        <Typography variant="subtitle1" className="sidebar-brand"></Typography>
      </Box>
      {/* <Divider /> */}
      <List sx={{ flex: 1, overflow: 'auto' }}>
        {menu.map((item) => (
          <ListItem key={item.to} disablePadding>
            <ListItemButton component={NavLink} to={item.to} onClick={() => setMobileOpen(false)}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon><LogoutIcon /></ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <Header handleDrawerToggle={handleDrawerToggle} />
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }} aria-label="admin folders">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          PaperProps={{ className: 'admin-drawer admin-drawer-mobile' }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              top: 0, // make it start from top
              height: '100vh', // full height
            },
          }}
          className='admin-drawer'
          open
        >
          {drawer}
        </Drawer>

      </Box>
      <Box
        component="main"
        className="admin-content"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          mt: '64px', // height of AppBar
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminLayout;
